import { Paper, Tabs, Tab, Grid2 } from "@mui/material";
import * as React from 'react';
import { useState, useEffect, ReactNode } from "react";
import {
    SupervisorAccountOutlined, PersonOutlined, FolderOutlined, NearMeOutlined,
    CreateNewFolderOutlined, PersonAddOutlined, BarChartOutlined, MailOutline
} from '@mui/icons-material';
import Chip from "@mui/material/Chip";
import * as api from 'shared/services/reportingApi';
import { List, StringListItem, ListItem, IListItemProps } from '../../ui/Lists';
import { Checkbox, LabelledCheckbox } from '../../ui/LabelledCheckbox';
import { IConfirmProps } from '../../ui/ConfirmationBox';
import '../../ui/ConfirmationBox.css';
import { TabPanel } from '../../ui/TabPanel';
import { ReactComponent as ReportingIcon } from "assets/icons/reports-filled.svg";
import { ReactComponent as ActionFields } from "assets/icons/action-fields-outlined.svg";
import { ReactComponent as BuildCircle } from "assets/icons/build-circle.svg";
import { ReactComponent as DashboardCustomise } from "assets/icons/dashboard-customize.svg";
import { ReactComponent as AddChart } from "assets/icons/add-chart.svg";
import "react-datepicker/dist/react-datepicker.css";
import './Reporting.css';
import { CleverString } from '../../ui/CleverString';
import PortalPage from '../PortalPage';
import { IModalBoxProps } from '../../ui/ModalBox';
import { DetailFormWrapper } from '../../ui/DetailForm';
import { AdminUserModel } from '../../../shared/models/api/models';
import { UserManagementModule } from '../administration/user-management-data';
import { userAdminApi } from '../../../shared/services/userApi';
import '../administration/administration.css';

import { ICheckboxListProps, IGridSizes, ISelectedExportColumn, standardGridSizes, ExportColumnList, ExportGrid } from './ExportGrid'
import { blobDownload } from './blobDownload';
import DateRangePicker from './DateRangePicker';

const dateToStringFormatter = (value: string) =>
    <div>{!value ? "" : (new Date(value)).toLocaleString()}</div>;

const booleanToYesNoFormatter = (value: boolean, trueValue = "Yes", falseValue = "No") =>
    <div>{!value ? falseValue : trueValue}</div>;

const stringArrayToString = (value: string[], separator = ", ") => value.join(separator);

const emailFormatter = (value: Date) => {
    return (
        <a href={"mailto:" + value} target="_blank">{value.toString()}</a>
    );
};

const CheckboxList = <T,>(props: ICheckboxListProps<T>) => {
    const gridSizes = props as IGridSizes;

    return (
        <Grid2 container>
            {props.items.map(item => <Grid2 {...gridSizes} key={props.buildKey(item)}>
                <LabelledCheckbox
                    checkedValue={props.isSelected(item)}
                    toggle={() => props.toggle(item)}>{props.content(item)}</LabelledCheckbox>
            </Grid2>)}
        </Grid2>
    );
};


const sortByComplianceReport = (a: api.ExportColumn, b: api.ExportColumn) => {
    if (a.defaultForComplianceReport && b.defaultForComplianceReport) {
        if (a.defaultForComplianceReport < b.defaultForComplianceReport) return -1;
        if (a.defaultForComplianceReport > b.defaultForComplianceReport) return 1;
    }
    return 0;
};

export const UserListItem = (props: IListItemProps<api.User>) =>
    <div className="list-item user-list-item" onClick={() => props.toggle(props.item)}>
        <LabelledCheckbox checkedValue={props.selected === true}><span className="name">{props.item.fullName}</span></LabelledCheckbox>
        <div>
            {props.item.city &&
                <span className="location" ><NearMeOutlined /> {props.item.city}</span>
            }

            {props.item.supervisor.length > 0 &&
                <span className="supervisor" ><SupervisorAccountOutlined />{props.item.supervisor}</span>
            }

            {!props.item.city && props.item.email.length > 0 &&
                <span className="email" ><MailOutline />{props.item.email} </span>
            }

        </div>
    </div>;

export const DocumentTypeListItem = (props: IListItemProps<api.DocumentType>) => {
    const isSelected = (doc: api.DocumentType): boolean => {
        if (!props) {
            return false;
        }

        if (!props.selectedItems) {
            return false;
        }

        return props.selectedItems.findIndex(d => doc.id === d.id) > -1;
    };

    return (
        <>
            {(props.previousItem == null || props.previousItem.module !== props.item.module) &&
                <div className="document-type-module">{props.item.module}</div>
            }

            <ListItem {...props}>
                <LabelledCheckbox checkedValue={props.selected === true}>
                    {props.item.name}
                </LabelledCheckbox>
            </ListItem >

            {props.item.subTypes?.map(st =>
                <div key={st.id} style={{ paddingLeft: ((props.indent ?? 1) * 30) + "px" }}>
                    <DocumentTypeListItem
                        selectedItems={props.selectedItems}
                        item={st}
                        toggle={props.toggle}
                        selected={isSelected(st)}
                        previousItem={props.item}
                        indent={(props.indent ?? 1) + 1}
                    >
                    </DocumentTypeListItem>
                </div>
            )}
        </>
    );
};

export const DocumentListItem = (props: IListItemProps<api.Document<api.DocumentMetadata>>) =>
    <div className="list-item" onClick={() => props.toggle(props.item)}>
        <Checkbox checkedValue={props.selected === true} />
        <span className="document-type">{props.item.type}</span>
        {props.item.subType && <span className="document-sub-type">{props.item.subType}</span>}
        {props.item.metadata.title}
    </div>;

interface IChipsProps<T> {
    values: T[],
    removeItem: (item: T) => void,
    prefix?: any
}
const CleverStringChips = (props: IChipsProps<CleverString>) =>
    <>
        {props.values &&
            props.values.map(cs =>
                <Chip
                    key={cs.toKebabCase()}
                    onDelete={() => { props.removeItem(cs); }}
                    label={<>
                        <b>{props.prefix ? props.prefix + ": " : ""}</b>{cs.toSentenceCase().toString()}
                    </>}
                />
            )
        }
    </>;

const UserChips = (props: IChipsProps<api.User>) =>
    <>
        {props.values &&
            props.values.map(usr =>
                <Chip
                    key={usr.id}
                    onDelete={() => { props.removeItem(usr); }}
                    label={<>
                        <b>{props.prefix ? props.prefix + ": " : ""}</b>{usr.fullName}
                    </>} />
            )
        }
    </>;

const DocumentChips = (props: IChipsProps<api.Document<api.DocumentMetadata>>) =>
    <>
        {props.values &&
            props.values.map(doc =>
                <Chip
                    key={doc.id}
                    onDelete={() => { props.removeItem(doc); }}
                    label={<>
                        <b>{props.prefix ? props.prefix + ": " : ""}</b>{doc.metadata.title}
                    </>}
                />
            )
        }
    </>;

const DocumentTypeChips = (props: IChipsProps<api.DocumentType>) =>
    <>
        {props.values &&
            props.values.map(dt =>
                <Chip
                    key={dt.id}
                    onDelete={() => { props.removeItem(dt); }}
                    label={<>
                        <b>{props.prefix ? props.prefix + ": " : ""}</b>{dt.name}
                    </>}
                />
            )
        }
    </>;

const Chips = (props: IChipsProps<string>) =>
    <>
        {props.values &&
            props.values.map(val =>
                <Chip
                    key={val}
                    onDelete={() => { props.removeItem(val); }}
                    label={<>
                        <b>{props.prefix ? props.prefix + ": " : ""}</b>{val}
                    </>}
                />
            )
        }
    </>;

const UserDocumentReport = () => {
    const MaxResultsPerReport = 60000;

    const [exportState, setExportState] = useState("ready" as "ready" | "busy");

    const [stateVersion, setStateVersion] = useState(0);
    const [selectedDocuments, setSelectedDocuments] = useState([] as api.Document<api.DocumentMetadata>[]);

    const [selectedUsers, setSelectedUsers] = useState([] as api.User[]);

    const [exportUsers, setExportUsers] = useState(false);
    const [exportDocuments, setExportDocuments] = useState(false);

    useEffect(() => {
        let newExportColumns = exportColumns.map(c => c.definition);

        //Deselect any columns from a groups not enabled
        if (!exportUsers) {
            newExportColumns = newExportColumns.filter(selectedCol => !userColumns.some(col => col.name == selectedCol.name));
        }

        if (!exportDocuments) {
            newExportColumns = newExportColumns.filter(selectedCol => !documentColumns.some(col => col.name == selectedCol.name));
        }

        if (!exportUsers || !exportDocuments) {
            newExportColumns = newExportColumns.filter(selectedCol => !actionColumns.some(col => col.name == selectedCol.name));
        }

        //Select defaults from groups that are enabled with no current columns selected
        let selectedUserColumns = exportColumns.filter(selectedCol => userColumns.some(col => col.name == selectedCol.definition.name));
        if (exportUsers && selectedUserColumns.length === 0) {
            newExportColumns = newExportColumns.concat(userColumns.filter(c => c.defaultForComplianceReport));
        }

        let selectedDocumentColumns = exportColumns.filter(selectedCol => documentColumns.some(col => col.name == selectedCol.definition.name));
        if (exportDocuments && selectedDocumentColumns.length === 0) {
            newExportColumns = newExportColumns.concat(documentColumns.filter(c => c.defaultForComplianceReport));
        }

        let selectedActionColumns = exportColumns.filter(selectedCol => actionColumns.some(col => col.name == selectedCol.definition.name));
        if (exportUsers && exportDocuments && selectedActionColumns.length === 0) {
            newExportColumns = newExportColumns.concat(actionColumns.filter(c => c.defaultForComplianceReport));
        }

        newExportColumns.sort(sortByComplianceReport);
        setExportColums(newExportColumns.map((c, index) => { return { seq: index, definition: c }; }));

    }, [exportUsers, exportDocuments]);

    const [exportResults, setExportResults] = useState(null as api.UsersDocumentsResult | null);

    const [allUsers, setAllUsers] = useState([] as api.User[]);
    const [selectAllUsers, setSelectAllUsers] = useState(false);
    const [usersByNameFilter, setUsersByNameFilter] = useState("");
    const [usersByNameExpanded, setUsersByNameExpanded] = useState(false);

    const [usersInMyTeam, setUsersInMyTeam] = useState([] as api.User[]);
    const [usersInMyTeamFilter, setUsersInMyTeamFilter] = useState("");
    const [usersInMyTeamListExpanded, setUsersInMyTeamListExpanded] = useState(false);

    const [allSupervisors, setAllSupervisors] = useState([] as api.User[]);
    const [selectedSupervisors, setSelectedSupervisors] = useState([] as api.User[]);
    const [usersBySupervisorFilter, setUsersBySupervisorFilter] = useState("");
    const [usersBySupervisorExpanded, setUsersBySupervisorExpanded] = useState(false);

    const [allDepartments, setAllDepartments] = useState([] as string[]);
    const [allParentDepartments, setAllParentDepartments] = useState([] as string[]);
    const [selectedDepartments, setSelectedDepartments] = useState([] as string[]);
    const [selectedParentDepartments, setSelectedParentDepartments] = useState([] as string[]);
    const [usersByDepartmentFilter, setUsersByDepartmentFilter] = useState("");
    const [usersByParentDepartmentFilter, setUsersByParentDepartmentFilter] = useState("");
    const [usersByDepartmentExpanded, setUsersByDepartmentExpanded] = useState(false);
    const [usersByParentDepartmentExpanded, setUsersByParentDepartmentExpanded] = useState(false);

    const [selectedUserStatuses, setSelectedUserStatuses] = useState([] as string[]);
    const [userStatuses, setUserStatuses] = useState([] as string[]);

    const [allDocuments, setAllDocuments] = useState([] as api.Document<api.DocumentMetadata>[]);
    const [selectAllDocuments, setSelectAllDocuments] = useState(false);

    const [documentsByNameFilter, setDocumentsByNameFilter] = useState("");
    const [documentsByNameExpanded, setDocumentsByNameExpanded] = useState(false);

    const [documentStatuses, setDocumentStatuses] = useState([] as CleverString[]);
    const [selectedDocumentStatuses, setSelectedDocumentStatuses] = useState([] as CleverString[]);

    const [documentTypes, setDocumentTypes] = useState([] as api.DocumentType[]);
    const [selectedDocumentTypes, setSelectedDocumentTypes] = useState([] as api.DocumentType[]);
    const [documentsByTypeFilter, setDocumentsTypeFilter] = useState("");
    const [documentsByTypeExpanded, setDocumentsByTypeExpanded] = useState(false);

    const [allPublishers, setAllPublishers] = useState([] as api.User[]);
    const [selectedPublishers, setSelectedPublishers] = useState([] as api.User[]);
    const [documentsByPublisherFilter, setDocumentsPublisherFilter] = useState("");
    const [documentsByPublisherExpanded, setDocumentsByPublisherExpanded] = useState(false);

    const [publishedDateFrom, setPublishedDateFrom] = useState(null as Date | null);
    const [publishedDateTo, setPublishedDateTo] = useState(null as Date | null);

    const setPublishedDateRange = (dateFrom: Date | null, dateTo: Date | null): void => {
        setPublishedDateFrom(dateFrom);
        setPublishedDateTo(dateTo);
    };

    const refreshDepartmentsFromResult = async (result: string[]) => {
        if (result) setAllDepartments(result);
    };

    const refreshParentDepartmentsFromResult = async (result: string[]) => {
        if (result) setAllParentDepartments(result);
    };

    const refreshUserStatusesFromResult = async (result: string[]) => {
        if (result) setUserStatuses(result);
    };

    const refreshDocumentStatusesFromResult = async (result: CleverString[]) => {
        if (result) setDocumentStatuses(result);
    };

    const refreshUsersFromResult = async (result: api.User[]) => {
        if (result) setAllUsers(result);
    };

    const refreshSupervisorsFromResult = async (result: api.User[]) => {
        if (result) setAllSupervisors(result);
    };


    const refreshPublishersFromResult = async (result: api.User[]) => {
        if (result) setAllPublishers(result);
    };

    const refreshUsersInMyTeamFromResult = async (result: api.User[]) => {
        if (result) setUsersInMyTeam(result);
    };

    const forceStateChange = () => {
        setStateVersion(stateVersion + 1);
    };

    const selectUsers = (items: api.User[]) => {
        selectUserItems(items, selectedUsers, setSelectedUsers);
    }

    const selectSupervisors = (items: api.User[]) => {
        selectUserItems(items, selectedSupervisors, setSelectedSupervisors);
    }

    const selectPublishers = (items: api.User[]) => {
        selectUserItems(items, selectedPublishers, setSelectedPublishers);
    }

    const selectUserItems = (items: api.User[], stateItems: api.User[], setter: (items: api.User[]) => void) => {
        const newUsers = [...new Set([...stateItems, ...items])];
        userFilterAdded();
        setter(newUsers);
        forceStateChange();
    };

    const deselectUsers = (items: api.User[]) => {
        deselectUserItems(items, selectedUsers, setSelectedUsers);
    }

    const deselectSupervisors = (items: api.User[]) => {
        deselectUserItems(items, selectedSupervisors, setSelectedSupervisors);
    }

    const deselectPublishers = (items: api.User[]) => {
        deselectUserItems(items, selectedPublishers, setSelectedPublishers);
    }

    const deselectUserItems = (items: api.User[], stateItems: api.User[], setter: (items: api.User[]) => void) => {
        const newUsers = [...stateItems].filter(user => !items.includes(user));
        userFilterAdded();
        setter(newUsers);
        forceStateChange();
    };

    const selectDocuments = (items: api.DocumentType[]) => {
        selectDocumentItems(items, selectedDocumentTypes, setSelectedDocumentTypes);
    }

    const selectDocumentItems = (items: api.DocumentType[], stateItems: api.DocumentType[], setter: (items: api.DocumentType[]) => void) => {
        const newDocs = [...new Set([...stateItems, ...items])];
        documentFilterAdded();
        setter(newDocs);
        forceStateChange();
    };

    const deselectDocuments = (items: api.DocumentType[]) => {
        deselectDocumentItems(items, selectedDocumentTypes, setSelectedDocumentTypes);
    }

    const deselectDocumentItems = (items: api.DocumentType[], stateItems: api.DocumentType[], setter: (items: api.DocumentType[]) => void) => {
        const newDocs = [...stateItems].filter(user => !items.includes(user));
        documentFilterAdded();
        setter(newDocs);
        forceStateChange();
    };

    const refreshDocumentTypesFromResult = async (result: api.DocumentType[]) => {
        if (result) {
            // populate the sub types
            for (var t = 0; t < result.length; t++) {
                result[t].subTypes = result.filter(r => { return r.parentId === result[t].id; });
            }

            // set the list to the root notes
            setDocumentTypes(result.filter(r => { return r.parentId === null }));
        }
    };

    const refreshDocumentsFromServer = async (result: api.Document<api.DocumentMetadata>[]) => {
        if (result) {
            setAllDocuments(result);

            if (selectedDocuments.length > 0) {
                const currentDocumentIds = selectedDocuments.map(d => d.id);

                // re-select the documents using the IDs
                setSelectedDocuments(result.filter(d => currentDocumentIds.indexOf(d.id) > -1));
            }
        }
    };

    interface IRemoteUpdate {
        getData: () => Promise<any[] | null>;
        processData: (result: any[] | null) => void;
    };

    const remoteUpdates = [
        { getData: api.getDocumentTypes, processData: refreshDocumentTypesFromResult },
        { getData: api.getDocumentStatuses, processData: refreshDocumentStatusesFromResult },
        { getData: api.getDepartments, processData: refreshDepartmentsFromResult },
        { getData: api.getParentDepartments, processData: refreshParentDepartmentsFromResult },
        { getData: api.getUserStatuses, processData: refreshUserStatusesFromResult },
        { getData: api.getUsers, processData: refreshUsersFromResult },
        { getData: api.getSupervisors, processData: refreshSupervisorsFromResult },
        { getData: api.getPublishers, processData: refreshPublishersFromResult },
        { getData: api.getUsersSupervisedByCurrentUser, processData: refreshUsersInMyTeamFromResult },
        { getData: api.getAllDocuments, processData: refreshDocumentsFromServer }
    ] as IRemoteUpdate[];

    const processRemoteResults = (results: PromiseSettledResult<any[] | null>[]) => {
        results.forEach((result, i) => {
            if (result.status === "fulfilled" && result.value != null) {
                remoteUpdates[i].processData(result.value);
            }
            else {
                const errorMessage = `error retrieving ${remoteUpdates[i].getData.name}`;
                console.error(errorMessage);
                msgBox(errorMessage);
            }
        });

    }

    useEffect(() => {
        let mounted = true;

        const mount = async () => {
            let results = await Promise.allSettled(remoteUpdates.map(r => r.getData()));
            if (mounted && results) {
                processRemoteResults(results);
            }
        };

        mount();

        return () => { mounted = false };
    }, []);

    const getVisibleColumns = () => {
        return exportColumns
            .filter(xc => {
                if (exportUsers && userColumns.findIndex(uc => uc.name === xc.definition.name) > -1) {
                    return true
                }

                if (exportDocuments && documentColumns.findIndex(uc => uc.name === xc.definition.name) > -1) {
                    return true
                }

                if (exportUsers && exportDocuments && actionColumns.findIndex(uc => uc.name === xc.definition.name) > -1) {
                    return true
                }

                return false;
            });
    };

    const getExportFilter = (page: number) => {
        if (
            // user filters
            !selectAllUsers
            && selectedUsers.length === 0
            && selectedSupervisors.length === 0
            && selectedUserStatuses.length === 0
            && selectedDepartments.length === 0
            && selectedParentDepartments.length === 0

            // document filters
            && !selectAllDocuments
            && selectedDocuments.length === 0
            && selectedDocumentTypes.length === 0
            && selectedDocumentStatuses.length === 0
            && selectedPublishers.length === 0
            && publishedDateFrom == null
            && publishedDateTo == null
        ) {
            confirm("You haven't selected any data. Please make a selection to run a report.", ["OK"], "OK");
            return null;
        }

        if (exportColumns.length === 0) {
            confirm("You haven't selected any fields. Please make a selection to run a report.", ["OK"], "OK");
            return null;
        }

        return {
            users: exportUsers ? {
                ids: selectedUsers.map(u => u.id),
                supervisorIds: selectedSupervisors.map(s => s.id),
                statuses: selectedUserStatuses,
                departments: selectedDepartments,
                employeeTypes: [],
                parentDepartments: selectedParentDepartments
            } : null,
            documents: exportDocuments ? {
                ids: selectedDocuments.map(u => u.id),
                typeIds: selectedDocumentTypes.map(dt => dt.id),
                statuses: selectedDocumentStatuses,
                publishedDateFrom: publishedDateFrom,
                publishedDateTo: publishedDateTo,
                publisherIds: selectedPublishers.map(p => p.id)
            } : null,
            pageSize: pageSize,
            pageNumber: page,
            sort: sortOrder,
            fileType: "CSV",
            exportColumns: getVisibleColumns().map(c => { return { name: c.definition.name, title: c.definition.title } as api.ExportColumn })
        } as api.UsersDocumentsFilter;
    };

    const downloadExportResults = async () => {
        if (exportState === "ready") {
            const filter = getExportFilter(1);

            if (filter) {
                setExportState("busy");

                // set the pageSize to zero, so that we get all of the results
                filter.pageSize = 0;

                api.downloadResults(filter)
                    .then(file => {
                        setExportState("ready");
                        blobDownload(file);
                    })
                    .catch(e => {
                        const message = (e instanceof RangeError) ? e.message : "Error fetching data. ";
                        confirm(message, ["Close"], "Close");
                        setExportState("ready");
                    });
            }
        }
    };

    const refreshExportResults = async (page: number) => {
        if (exportState === "ready") {
            setPageNumber(page);

            const filter = getExportFilter(page);

            if (filter) {
                setExportState("busy");

                api.getUsersDocuments(filter)
                    .then(result => {
                        setExportState("ready");
                        setExportResults(result);
                        if (result.totalResults > MaxResultsPerReport) {
                            msgBox(<>
                                <div>Resulting data set too large.</div>
                                <div>Select less data or download full extract.</div>
                            </>);
                        }
                    })
                    .catch(e => {
                        const message = (e instanceof RangeError) ? e.message : "Error fetching data. ";
                        confirm(message, ["Close"], "Close");
                        setExportState("ready");
                    });
            }
        }
    };

    const loadUser = (id: number) => {
        return userAdminApi.getAsync(id);
    };

    const saveUser = (userToSave: AdminUserModel) => {
        return userAdminApi.updateAsync(userToSave);
    };

    const msgBox = (msg: ReactNode) => {
        setConfirmProps({
            message: msg,
            options: ["OK"],
            defaultOption: "OK",
            setResult: result => {
                setConfirmProps(null);
            }
        });
    };

    const userUpdated = (usr: AdminUserModel) => {
        // close the dialog
        setUserInfoProps(null);

        //tell the user it saved
        msgBox("Your request has been successfully completed.");

        // throwing an error at this stage, new functionality. can deal with it later if someone needs it fixed
        //refreshExportResults(pageNumber);
    };

    const showUserInfo = (userDoc: api.UserDocument<api.DocumentMetadataExtended>) => {
        const userId = userDoc?.user?.id;

        if (userId) {
            setUserInfoProps({
                className: "user-information-dialog",
                body:
                    <DetailFormWrapper
                        module={UserManagementModule}
                        viewName="view"
                        load={() => loadUser(userId)}
                        save={saveUser}
                        setConfirmationProps={setConfirmProps}
                        closeDialog={() => setUserInfoProps(null)}
                        onDataChanged={userUpdated}
                    />
            });
        }
    };

    const [userColumns] = useState([
        { title: "Name", name: "user.fullName", selected: false, defaultForComplianceReport: 1, infoDialog: showUserInfo },
        { title: "Employee type", name: "user.employeeType", selected: false },
        { title: "Company name", name: "user.companyName", selected: false },
        { title: "Supervisor", name: "user.supervisor", selected: false, defaultForComplianceReport: 3 },
        { title: "Title", name: "user.jobTitle", selected: false },
        { title: "Role", name: "user.role", selected: false },
        { title: "Department", name: "user.department", selected: false },
        { title: "Parent Department", name: "user.parentDepartment", selected: false },
        { title: "Division", name: "user.division", selected: false },
        { title: "City", name: "user.city", selected: false },
        { title: "Location", name: "user.officeLocation", selected: false },
        { title: "User status", name: "user.status", selected: false, defaultForComplianceReport: 5 },
        { title: "Last login", name: "user.lastLoginDate", selected: false, formatter: dateToStringFormatter },
        { title: "Email", name: "user.email", selected: false, defaultForComplianceReport: 6, formatter: emailFormatter },
        { title: "Mobile", name: "user.mobile", selected: false, formatter: emailFormatter }
    ] as api.ExportColumn[]);

    const [documentColumns] = useState([
        { title: "Document title", name: "document.metadata.title", selected: false, defaultForComplianceReport: 7 },
        { title: "Document number", name: "document.metadata.documentNumber", selected: false, defaultForComplianceReport: 8 },
        { title: "Document type", name: "document.type", selected: false, defaultForComplianceReport: 9 },
        { title: "Document sub-type", name: "document.subType", selected: false },
        { title: "MBS Rulebook", name: "document.metadata.cbtc", selected: false, formatter: booleanToYesNoFormatter },
        { title: "Rulebook", name: "document.metadata.traditional", selected: false, formatter: booleanToYesNoFormatter },
        { title: "Document status", name: "document.status", selected: false, defaultForComplianceReport: 10 },
        { title: "Version number", name: "document.metadata.version", selected: false },
        { title: "Publisher", name: "document.uploadedBy", selected: false },
        { title: "Published date", name: "document.metadata.publishDate", selected: false, formatter: dateToStringFormatter, defaultForComplianceReport: 11 },
        { title: "Effective date", name: "document.metadata.effectiveFromDate", selected: false, formatter: dateToStringFormatter },
        { title: "Expiry date", name: "document.metadata.expiryDate", selected: false, formatter: dateToStringFormatter },
        { title: "Last updated by", name: "document.metadata.modifiedBy", selected: false },
        { title: "Date of last update", name: "document.metadata.modifiedDate", selected: false, formatter: dateToStringFormatter },
        { title: "Related documents", name: "document.metadata.relatedDocuments", selected: false, formatter: stringArrayToString },
        { title: "Release notes", name: "document.metadata.releaseNotes", selected: false },
        { title: "Rescind Reason", name: "document.metadata.rescindDocumentReason", selected: false },
        { title: "File name", name: "document.fileName", selected: false },
        { title: "Require Acknowledgement", name: "document.metadata.requiresAcknowledgement", selected: false, formatter: booleanToYesNoFormatter },
        { title: "Document Department", name: "document.metadata.department", selected: false },
    ] as api.ExportColumn[]);

    const [actionColumns] = useState([
        { title: "Last action", name: "action.lastAction", selected: false, defaultForComplianceReport: 12 },
        { title: "Notified date", name: "action.notifyDate", selected: false, formatter: dateToStringFormatter, defaultForComplianceReport: 13 },
        { title: "Viewed date", name: "action.viewDate", selected: false, formatter: dateToStringFormatter, defaultForComplianceReport: 14 },
        { title: "Acknowledged date", name: "action.acknowledgedDate", selected: false, formatter: dateToStringFormatter, defaultForComplianceReport: 15 },
        { title: "Clarification requested date", name: "action.clarificationDate", selected: false, formatter: dateToStringFormatter, defaultForComplianceReport: 16 },
        { title: "Feedback message", name: "action.feedbackText", selected: false, defaultForComplianceReport: 16 },
        { title: "Clarification request message", name: "action.clarificationText", selected: false, defaultForComplianceReport: 18 }
    ] as api.ExportColumn[]);

    const [exportColumns, setExportColums] = useState([] as ISelectedExportColumn[]);

    const [sortOrder, setSortOrder] = useState([] as api.ISortConfig[]);
    useEffect(() => {
        if (exportResults && exportResults.totalResults > 0) {
            refreshExportResults(pageNumber);
        }
    }, [sortOrder]);

    const [pageSize, setPageSize] = useState(20);
    const [pageNumber, setPageNumber] = useState(1);

    const [confirmProps, setConfirmProps] = useState(null as IConfirmProps | null);
    const [userInfoProps, setUserInfoProps] = useState(null as IModalBoxProps | null);

    const [userFilterTab, setUserFilterTab] = useState(0);
    const [documentFilterTab, setDocumentFilterTab] = useState(0);

    const toggleItem = <T,>(
        item: T,
        arr: T[],
        comparerPredicate: (value: T) => boolean,
        setter: (value: T[]) => void
    ): boolean => {
        const idx = arr.findIndex(comparerPredicate);

        var result = false;

        if (idx === -1) {
            result = true;
            arr.push(item);
        } else {
            arr.splice(idx, 1);
        }

        setter(arr);
        forceStateChange();

        return result;
    };

    // when the user changes a user filter, turn on exporting users, and turn off "Select All Users"
    const userFilterAdded = () => {
        setExportUsers(true);
        setExportResults(null);

        if (selectAllUsers) {
            setSelectAllUsers(false);
        }
    }

    const toggleUser = (usr: api.User) => {
        if (toggleItem(usr, selectedUsers, u => { return u.id === usr.id }, setSelectedUsers) === true) {
            userFilterAdded();
        }
    };

    const toggleDepartment = (dpt: string): void => {
        if (toggleItem(dpt, selectedDepartments, d => { return d === dpt }, setSelectedDepartments) === true) {
            userFilterAdded();
        }
    };

    const toggleParentDepartment = (dpt: string): void => {
        if (toggleItem(dpt, selectedParentDepartments, d => { return d === dpt }, setSelectedParentDepartments) === true) {
            userFilterAdded();
        }
    };

    const toggleSupervisor = (usr: api.User): void => {
        if (toggleItem(usr, selectedSupervisors, u => { return u.id === usr.id }, setSelectedSupervisors) === true) {
            userFilterAdded();
        }
    };

    const toggleUserStatus = (status: string) => {
        if (toggleItem(status, selectedUserStatuses, us => { return us === status }, setSelectedUserStatuses) === true) {
            userFilterAdded();
        }
    };

    // when the user changes a document filter, turn on exporting documents, and turn off "Select All Documents"
    const documentFilterAdded = () => {
        setExportDocuments(true);
        setExportResults(null);

        if (selectAllDocuments) {
            setSelectAllDocuments(false);
        }
    }

    const toggleDocument = (doc: api.Document<api.DocumentMetadata>): void => {
        if (toggleItem(doc, selectedDocuments, d => { return d.id === doc.id }, setSelectedDocuments) === true) {
            documentFilterAdded();
        }
    };

    const toggleDocumentStatus = (status: CleverString) => {
        if (toggleItem(status, selectedDocumentStatuses, s => { return s === status }, setSelectedDocumentStatuses) === true) {
            documentFilterAdded();
        }
    };

    const toggleDocumentType = (type: api.DocumentType) => {
        if (toggleItem(type, selectedDocumentTypes, dt => { return dt.id === type.id }, setSelectedDocumentTypes) === true) {
            documentFilterAdded();
        }
    };

    const togglePublisher = (usr: api.User) => {
        if (toggleItem(usr, selectedPublishers, p => { return p.id === usr.id }, setSelectedPublishers) === true) {
            documentFilterAdded();
        }
    };

    const toggleExportColumn = (definition: api.ExportColumn) => {
        const idx: number = exportColumns.findIndex(c => { return c.definition.name == definition.name; });

        if (idx === -1) {
            // add the column
            exportColumns.push({
                seq: exportColumns.length,
                definition: definition
            })

            // update the state
            setExportColums(exportColumns);
        } else {
            // remove the column
            const cols = exportColumns;
            cols.splice(idx, 1);

            // update the sequence numbers for any subsequent columns, so that if we add another it we dont get duplicate sequence numbers
            for (var x = idx; x < cols.length; x++) {
                cols[x].seq = x;
            }

            // update the state with the refined list
            setExportColums(cols);
        }

        forceStateChange();
    };

    const userNameItemFilter = (item: api.User, filterText: string) => {
        const lcName = filterText.toLowerCase();

        return (
            (item.fullName).toLowerCase().indexOf(lcName) > -1
            || (item.email?.toLowerCase().indexOf(lcName) > -1)
        );
    };

    const userNameFilter = (name: string, users?: api.User[]) => {
        if (!users) {
            return [] as api.User[];
        }

        return users.filter(u => userNameItemFilter(u, name));
    };

    const documentNameItemFilter = (doc: api.Document<api.DocumentMetadata>, filterText: string) => {
        const lcName = filterText?.toLowerCase();

        return (
            doc.metadata.title.toLowerCase().indexOf(lcName) > -1
            || doc.metadata.documentNumber.toLowerCase().indexOf(lcName) > -1
            || doc.fileName.toLowerCase().indexOf(lcName) > -1
            || doc.type.toLowerCase().indexOf(lcName) > -1
            || doc.subType?.toLowerCase().indexOf(lcName) > -1
        );
    }

    const documentNameFilter = (name: string, documents?: api.Document<api.DocumentMetadata>[]) => {
        if (!documents) {
            return [] as api.Document<api.DocumentMetadata>[];
        }

        const result = documents.filter(doc => documentNameItemFilter(doc, name));

        return result;
    };

    const documentTypeNameContains = (item: api.DocumentType, name: string): boolean => {
        const lcFilterText = name.toLowerCase();

        if (item.name.toLowerCase().indexOf(lcFilterText) > -1) {
            return true;
        }

        if (item.subTypes) {
            for (var s = 0; s < item.subTypes.length; s++) {
                if (documentTypeNameContains(item.subTypes[s], lcFilterText)) {
                    return true;
                }
            }
        }

        return false;
    };

    const documentTypeNameFilter = (name: string, items?: api.DocumentType[]) => {
        if (!items) {
            return [] as api.DocumentType[];
        }

        const result = items.filter(item => documentTypeNameContains(item, name));

        return result;
    };

    const stringItemFilter = (item: string, filterText: string) => {
        return item?.toLowerCase().indexOf(filterText.toLowerCase()) > -1;
    };

    const filterStringArray = (name: string, items?: string[]) => {
        if (!items) return [] as string[];

        const result = items.filter(v => stringItemFilter(v, name));

        return result;
    };

    const confirm = async (message: string, opts?: string[], defaultOption?: string, cancelOption?: string): Promise<string> => {
        return new Promise<string>((resolve) => {
            setConfirmProps({
                message: message,
                options: opts ?? ["OK", "Cancel"],
                defaultOption: defaultOption,
                setResult: (value) => {
                    setConfirmProps({} as IConfirmProps);
                    resolve(value);
                }
            });
        });
    };

    const clearUserFilters = () => {
        setSelectedUsers([]);
        setSelectAllUsers(false);
        setSelectedUserStatuses([]);
        setSelectedDepartments([]);
        setSelectedParentDepartments([]);
        setSelectedSupervisors([]);
    };

    const setSelectAllUsersAndClearSelections = (value: boolean) => {
        if (value) {
            confirm("Are you sure you want to include all users? Including all users will reset all user filters.", ["All Users", "Cancel"], "All Users").then(result => {
                if (result === "All Users") {
                    setSelectedUsers([]);
                    setSelectedUserStatuses([]);
                    setSelectedDepartments([]);
                    setSelectedParentDepartments([]);
                    setSelectedSupervisors([]);

                    setSelectAllUsers(true);
                    setExportUsers(true);
                } else {
                    setSelectAllUsers(false);
                }
            });
        } else {
            setSelectAllUsers(value);
        }
        setExportResults(null);
    };

    const clearDocumentFilters = () => {
        setSelectedDocuments([]);
        setSelectAllDocuments(false);
        setSelectedDocumentStatuses([]);
        setSelectedDocumentTypes([]);
        setSelectedPublishers([]);
        setPublishedDateFrom(null);
        setPublishedDateTo(null);
    };

    const setSelectAllDocumentsAndClearSelections = (value: boolean) => {
        if (value) {
            confirm("Are you sure you want to include all documents? Including all users will reset all document filters.", ["All Documents", "Cancel"], "All Documents").then(result => {
                if (result === "All Documents") {
                    setSelectedDocuments([]);
                    setSelectedDocumentStatuses([]);
                    setSelectedDocumentTypes([]);
                    setSelectedPublishers([]);
                    setPublishedDateFrom(null);
                    setPublishedDateTo(null);

                    setSelectAllDocuments(true);
                    setExportDocuments(true);
                } else {
                    setSelectAllDocuments(false);
                }
            });
        } else {
            setSelectAllDocuments(value);
        }
        setExportResults(null);
    };

    const userFiltersExist = () => {
        return (selectAllUsers
            || selectedUsers.length > 0
            || selectedSupervisors.length > 0
            || selectedDepartments.length > 0
            || selectedUserStatuses.length > 0);
    };

    const toggleExportUsers = () => {
        if (exportUsers && userFiltersExist()) {

            confirm(
                "Disabling user export will clear all user filters, are you sure?",
                ["Disable user export", "Cancel"],
                "Disable user export"
            ).then(
                result => {
                    if (result !== "Cancel") {
                        clearUserFilters();
                        setExportUsers(false);
                    }
                }
            );
        } else {
            setExportUsers(!exportUsers);
        }
    };

    const documentFiltersExist = () => {
        return (selectAllDocuments
            || selectedDocuments.length > 0
            || selectedDocumentTypes.length > 0
            || selectedDocumentStatuses.length > 0
            || selectedPublishers.length > 0
            || publishedDateFrom !== null
            || publishedDateTo !== null);
    };

    const toggleExportDocuments = () => {
        if (exportDocuments && documentFiltersExist()) {
            confirm(
                "Disabling document export will clear all document filters, are you sure?",
                ["Disable document export", "Cancel"],
                "Disable document export"
            ).then(
                result => {
                    if (result !== "Cancel") {
                        clearDocumentFilters();
                        setExportDocuments(false);
                    }
                }
            );
        } else {
            setExportDocuments(!exportDocuments);
        }
    };

    const downloadComplianceReport = async () => {
        api.downloadFullComplianceReport()
            .then(file => {
                blobDownload(file);
            })
            .catch((e) => {
                const message = (e instanceof RangeError) ? e.message : "Error fetching data. ";
                confirm(message, ["Close"], "Close");
            });
    };

    const allExportTypeCheckedValue = (columns: api.ExportColumn[]) => {
        const selectedColumnCount = exportColumns
            .map(selCol => selCol.definition)
            .filter(exportCol => columns.includes(exportCol))
            .length;

        if (selectedColumnCount === 0) {
            return false;
        }

        if (selectedColumnCount === columns.length) {
            return true;
        }

        return 'partial';
    }

    const selectColumns = (columns: api.ExportColumn[]) => {
        let newExportColumns = [...exportColumns];
        columns.forEach(exportCol => {
            if (!newExportColumns.map(selCol => selCol.definition).includes(exportCol)) {
                newExportColumns.push({
                    seq: newExportColumns.length,
                    definition: exportCol
                });
            }
        });
        setExportColums(newExportColumns);
    }

    const unselectColumns = (selectedColumns: api.ExportColumn[]) => {
        setExportColums(exportColumns.filter(selCol => !selectedColumns.includes(selCol.definition)));
    }

    const toggleAllExportTypeChecked = (columns: api.ExportColumn[]) => {
        const selectedColumns = exportColumns
            .map(selCol => selCol.definition)
            .filter(exportCol => columns.includes(exportCol));

        // All columns currently selected, unselect all from this group
        if (selectedColumns.length === columns.length) {
            unselectColumns(selectedColumns);
            return;
        }

        // Empty or partial selection, select all from this group not already selected
        selectColumns(columns);
    }

    return (
        <PortalPage module=""
            page="reporting"
            title={"User / Document reporting"}
            route={"/reporting/user-document"}
            loadingState={exportState}
            icon={ReportingIcon}
            confirmationProps={confirmProps}
            modalProps={userInfoProps}>


            <div className="main-body-container reporting-module mb-5">

                <div className="section-1">
                    <div className="header">
                        <span className="section-1 icon"><BuildCircle /></span>
                        <span className="section-1 text">Download full report</span>
                    </div>
                </div>

                <div className="section-3 pl-4 pb-4">
                    <button onClick={downloadComplianceReport} className="button-face">Download full compliance report</button>
                </div>

            </div>


            <div className="main-body-container reporting-module">

                <div className="section-1">
                    <div className="header">
                        <span className="section-1 icon"><BuildCircle /></span>
                        <span className="section-1 text">Build a report</span>
                    </div>
                </div>

                <div className="section-2">
                    <div className="header">
                        <span className="icon"><AddChart /></span>
                        <span className="text">Add data</span>
                    </div>
                </div>

                <div className="section-3">
                    <div className="header" onClick={toggleExportUsers}>
                        <Checkbox checkedValue={exportUsers} />
                        <span className="icon"><PersonOutlined /></span>
                        <span className="text">User</span>
                    </div>
                    <div className="divider" />
                </div>

                <div className="section-4">
                    <div className="body">
                        <LabelledCheckbox
                            checkedValue={selectAllUsers}
                            toggle={() => { setSelectAllUsersAndClearSelections(!selectAllUsers); }}>Select all users</LabelledCheckbox>
                    </div>
                    <div className="body">or</div>
                    <div className="body">
                        <Paper>
                            <Tabs value={userFilterTab} variant="scrollable" onChange={(e, v) => { setUserFilterTab(v); }}>
                                <Tab label="Find a user" />
                                <Tab label="Users in my team" />
                                <Tab label="Users by supervisor" />
                                <Tab label="Users by team" />
                                <Tab label="Users by department" />
                                <Tab label="Users by status" />
                            </Tabs>

                            <TabPanel value={userFilterTab} index={0}>
                                <List<api.User>
                                    dataSource={allUsers}
                                    searchDelayMs={1}
                                    displayLoadingData={true}
                                    refreshFilterOnDataSourceChange={true}
                                    listItemType={UserListItem}
                                    filterText={usersByNameFilter}
                                    setFilterText={setUsersByNameFilter}
                                    expanded={usersByNameExpanded}
                                    setExpanded={setUsersByNameExpanded}
                                    buildKey={item => item.id}
                                    filter={userNameFilter}
                                    toggleSelected={toggleUser}
                                    selectItems={selectUsers}
                                    deselectItems={deselectUsers}
                                    noResultsMessage="No users found"
                                    filterTextPlaceholder="Enter first name, last name or email address"
                                    selected={selectedUsers} />
                            </TabPanel>

                            <TabPanel value={userFilterTab} index={1}>
                                <List<api.User>
                                    listItemType={UserListItem}
                                    filterText={usersInMyTeamFilter}
                                    setFilterText={setUsersInMyTeamFilter}
                                    expanded={usersInMyTeamListExpanded}
                                    setExpanded={setUsersInMyTeamListExpanded}
                                    buildKey={item => item.id}
                                    filter={userNameFilter}
                                    itemFilter={userNameItemFilter}
                                    dataSource={usersInMyTeam}
                                    searchDelayMs={1}
                                    displayLoadingData={true}
                                    refreshFilterOnDataSourceChange={true}
                                    toggleSelected={toggleUser}
                                    selectItems={selectUsers}
                                    deselectItems={deselectUsers}
                                    noResultsMessage="No users found"
                                    filterTextPlaceholder="Enter first name, last name or email address"
                                    selected={selectedUsers} />
                            </TabPanel>

                            <TabPanel value={userFilterTab} index={2}>
                                <List<api.User>
                                    listItemType={UserListItem}
                                    filterText={usersBySupervisorFilter}
                                    setFilterText={setUsersBySupervisorFilter}
                                    expanded={usersBySupervisorExpanded}
                                    setExpanded={setUsersBySupervisorExpanded}
                                    buildKey={item => item.id}
                                    filter={userNameFilter}
                                    itemFilter={userNameItemFilter}
                                    dataSource={allSupervisors}
                                    searchDelayMs={1}
                                    displayLoadingData={true}
                                    refreshFilterOnDataSourceChange={true}
                                    toggleSelected={toggleSupervisor}
                                    selectItems={selectSupervisors}
                                    deselectItems={deselectSupervisors}
                                    noResultsMessage="No supervisors found"
                                    filterTextPlaceholder="Enter first name, last name or email address"
                                    selected={selectedSupervisors} />
                            </TabPanel>

                            <TabPanel value={userFilterTab} index={3}>
                                <List<string>
                                    listItemType={StringListItem}
                                    filterText={usersByDepartmentFilter}
                                    setFilterText={setUsersByDepartmentFilter}
                                    expanded={usersByDepartmentExpanded}
                                    setExpanded={setUsersByDepartmentExpanded}
                                    dataSource={allDepartments}
                                    searchDelayMs={1}
                                    displayLoadingData={true}
                                    refreshFilterOnDataSourceChange={true}
                                    buildKey={item => item}
                                    filter={filterStringArray}
                                    itemFilter={stringItemFilter}
                                    toggleSelected={toggleDepartment}
                                    noResultsMessage="No departments found"
                                    selected={selectedDepartments} />
                            </TabPanel>

                            <TabPanel value={userFilterTab} index={4}>
                                <List<string>
                                    listItemType={StringListItem}
                                    filterText={usersByParentDepartmentFilter}
                                    setFilterText={setUsersByParentDepartmentFilter}
                                    expanded={usersByParentDepartmentExpanded}
                                    setExpanded={setUsersByParentDepartmentExpanded}
                                    dataSource={allParentDepartments}
                                    searchDelayMs={1}
                                    displayLoadingData={true}
                                    refreshFilterOnDataSourceChange={true}
                                    buildKey={item => item}
                                    filter={filterStringArray}
                                    itemFilter={stringItemFilter}
                                    toggleSelected={toggleParentDepartment}
                                    noResultsMessage="No departments found"
                                    filterTextPlaceholder="Enter department name"
                                    selected={selectedParentDepartments} />
                            </TabPanel>

                            <TabPanel value={userFilterTab} index={5}>
                                <CheckboxList<string>
                                    items={userStatuses}
                                    toggle={toggleUserStatus}
                                    buildKey={item => item}
                                    {...standardGridSizes}
                                    isSelected={item => selectedUserStatuses.indexOf(item) > -1}
                                    content={item => item}
                                />
                            </TabPanel>
                        </Paper>

                        <span>
                            <UserChips prefix="User" values={selectedUsers} removeItem={toggleUser} />
                            <UserChips prefix="Supervisor" values={selectedSupervisors} removeItem={toggleSupervisor} />
                            <Chips prefix="Department" values={selectedDepartments} removeItem={toggleDepartment} />
                            <Chips prefix="Department" values={selectedParentDepartments} removeItem={toggleParentDepartment} />
                            <Chips prefix="Status" values={selectedUserStatuses} removeItem={toggleUserStatus} />
                            &nbsp;
                            {selectAllUsers &&
                                <Chip onDelete={() => { setSelectAllUsersAndClearSelections(false); }} label={<><b>User: </b>All</>}></Chip>
                            }
                            &nbsp;
                        </span>
                    </div>
                </div>

                <div className="section-3">
                    <div className="header" onClick={toggleExportDocuments}>
                        <Checkbox checkedValue={exportDocuments} />
                        <span className="icon"><FolderOutlined /></span>
                        <span className="text">Document</span>
                    </div>
                    <div className="divider" />
                </div>

                <div className="section-4">
                    <div className="body top-border">
                        <LabelledCheckbox
                            checkedValue={selectAllDocuments}
                            toggle={() => { setSelectAllDocumentsAndClearSelections(!selectAllDocuments); }}>Select all documents</LabelledCheckbox>
                    </div>

                    <div className="body">or</div>
                    <div className="body">
                        <Paper>
                            <Tabs value={documentFilterTab} variant="scrollable" onChange={(e, v) => { setDocumentFilterTab(v); }}>
                                <Tab label="Find a document" />
                                <Tab label="Document type" />
                                <Tab label="Document status" />
                                <Tab label="Published date range" />
                                <Tab label="Publisher" />
                            </Tabs>

                            <TabPanel value={documentFilterTab} index={0} className="tab-panel-documents">
                                <List<api.Document<api.DocumentMetadata>>
                                    listItemType={DocumentListItem}
                                    filterText={documentsByNameFilter}
                                    setFilterText={setDocumentsByNameFilter}
                                    expanded={documentsByNameExpanded}
                                    setExpanded={setDocumentsByNameExpanded}
                                    buildKey={item => item.id}
                                    dataSource={allDocuments}
                                    searchDelayMs={1}
                                    displayLoadingData={true}
                                    refreshFilterOnDataSourceChange={true}
                                    filter={documentNameFilter}
                                    itemFilter={documentNameItemFilter}
                                    toggleSelected={toggleDocument}
                                    noResultsMessage="No documents found"
                                    filterTextPlaceholder="Enter document title, document number or keyword"
                                    selected={selectedDocuments} />
                            </TabPanel>

                            <TabPanel value={documentFilterTab} index={1}>
                                <List<api.DocumentType>
                                    listItemType={DocumentTypeListItem}
                                    filterText={documentsByTypeFilter}
                                    setFilterText={setDocumentsTypeFilter}
                                    expanded={documentsByTypeExpanded}
                                    setExpanded={setDocumentsByTypeExpanded}
                                    buildKey={item => item.id}
                                    dataSource={documentTypes}
                                    searchDelayMs={1}
                                    displayLoadingData={true}
                                    refreshFilterOnDataSourceChange={true}
                                    filter={documentTypeNameFilter}
                                    itemFilter={documentTypeNameContains}
                                    toggleSelected={toggleDocumentType}
                                    selectItems={selectDocuments}
                                    deselectItems={deselectDocuments}
                                    noResultsMessage="No documents types found"
                                    selected={selectedDocumentTypes} />
                            </TabPanel>

                            <TabPanel value={documentFilterTab} index={2} className="tab-panel-status">
                                <CheckboxList<CleverString>
                                    items={documentStatuses}
                                    toggle={toggleDocumentStatus}
                                    buildKey={item => item.toString()}
                                    {...standardGridSizes}
                                    isSelected={item => selectedDocumentStatuses.indexOf(item) > -1}
                                    content={item => { return item.toSentenceCase().toString(); }}
                                />
                            </TabPanel>

                            <TabPanel value={documentFilterTab} index={3}>
                                <DateRangePicker
                                    className="date-range-picker"
                                    fromDate={publishedDateFrom ?? undefined}
                                    toDate={publishedDateTo ?? undefined}
                                    setter={(f, t) => {
                                        setPublishedDateRange(f ? f : null, t ? t : null);
                                    }}
                                />
                            </TabPanel>

                            <TabPanel value={documentFilterTab} index={4}>
                                <List<api.User>
                                    listItemType={UserListItem}
                                    filterText={documentsByPublisherFilter}
                                    setFilterText={setDocumentsPublisherFilter}
                                    expanded={documentsByPublisherExpanded}
                                    setExpanded={setDocumentsByPublisherExpanded}
                                    buildKey={item => item.id}
                                    dataSource={allPublishers}
                                    searchDelayMs={1}
                                    displayLoadingData={true}
                                    refreshFilterOnDataSourceChange={true}
                                    filter={userNameFilter}
                                    toggleSelected={togglePublisher}
                                    selectItems={selectPublishers}
                                    deselectItems={deselectPublishers}
                                    noResultsMessage="No publishers found"
                                    filterTextPlaceholder="Enter first name, last name or email address"
                                    selected={selectedPublishers} />
                            </TabPanel>
                        </Paper>

                        <span>
                            <DocumentChips prefix="Doc" values={selectedDocuments} removeItem={toggleDocument} />
                            <DocumentTypeChips prefix="Type" values={selectedDocumentTypes} removeItem={toggleDocumentType} />
                            <CleverStringChips prefix="Status" values={selectedDocumentStatuses} removeItem={toggleDocumentStatus} />
                            <UserChips prefix="Publisher" values={selectedPublishers} removeItem={togglePublisher} />
                            {selectAllDocuments &&
                                <Chip onDelete={() => { setSelectAllDocumentsAndClearSelections(false); }} label={<><b>Document: </b>All</>}></Chip>
                            }
                            &nbsp;
                        </span>
                    </div>
                </div>

                <div className={`section-2 ${!exportUsers && !exportDocuments ? "d-none" : ""}`}>
                    <div className="header">
                        <span className="icon"><DashboardCustomise /></span>
                        <span className="text">Customise report fields</span>
                    </div>
                </div>

                {exportUsers && <>
                    <div className="section-3">
                        <div className="header">
                            <Checkbox
                                checkedValue={allExportTypeCheckedValue(userColumns)}
                                toggle={() => toggleAllExportTypeChecked(userColumns)} />
                            <PersonAddOutlined className="ml-3" />
                            <span className="icon">User Fields</span>
                        </div>
                        <div className="divider" />
                    </div>

                    <div className="section-4">
                        <div className="body">
                            <ExportColumnList {...standardGridSizes} columns={userColumns} toggle={toggleExportColumn} selected={exportColumns} />
                        </div>
                    </div>
                </>}

                {exportDocuments && <>
                    <div className="section-3">
                        <div className="header">
                            <Checkbox
                                checkedValue={allExportTypeCheckedValue(documentColumns)}
                                toggle={() => toggleAllExportTypeChecked(documentColumns)} />
                            <CreateNewFolderOutlined className="ml-3" />
                            <span className="icon">Document Fields</span>
                        </div>
                        <div className="divider" />
                    </div>

                    <div className="section-4">
                        <div className="body">
                            <ExportColumnList {...standardGridSizes} columns={documentColumns} toggle={toggleExportColumn} selected={exportColumns} />
                        </div>
                    </div>
                </>}

                {exportUsers && exportDocuments && <>
                    <div className="section-3">
                        <div className="header">
                            <Checkbox
                                checkedValue={allExportTypeCheckedValue(actionColumns)}
                                toggle={() => toggleAllExportTypeChecked(actionColumns)} />
                            <ActionFields className="ml-3" />
                            <span className="icon">Action Fields</span>
                        </div>
                        <div className="divider" />
                    </div>

                    <div className="section-4">
                        <div className="body">
                            <ExportColumnList {...standardGridSizes} columns={actionColumns} toggle={toggleExportColumn} selected={exportColumns} />
                        </div>
                    </div>
                </>}

                {(exportUsers || exportDocuments) &&
                    <div className="section-3">
                        <div className="body" style={{ textAlign: "center" }}>
                            <button className={"button-face export-state-" + exportState} onClick={() => { refreshExportResults(1); }}>Fetch data</button>
                        </div>
                    </div>
                }

                {((exportState === 'busy')) &&
                    <div className="export-busy-container">
                        <div className="export-busy-message">We're fetching your data...</div>
                    </div>
                }

                {(exportUsers || exportDocuments) && exportResults &&
                    <>
                        <div className="section-1 results">
                            <div className="header">
                                <span className="icon"><BarChartOutlined /></span>
                                <span className="text">Results</span>
                            </div>
                        </div>
                        {exportResults.totalResults == 0 ?
                            <div className="export-grid-no-results">No results are available for the current report</div>
                            :
                            <>
                                <div className="section-2 results">
                                    <div className="body">
                                        <ExportGrid
                                            columns={exportColumns}
                                            results={exportResults}
                                            pageNumber={pageNumber}
                                            pageSize={pageSize}
                                            state={exportState}
                                            moveToPage={refreshExportResults}
                                            setPageSize={setPageSize}
                                            sortOrder={sortOrder}
                                            removeColumn={col => { toggleExportColumn(col); }}
                                            setSortOrder={
                                                (arr) => {
                                                    setSortOrder(arr);
                                                    forceStateChange();
                                                }
                                            } />
                                    </div>
                                </div>
                                <div className="section-3 results">
                                    <div className="body" style={{ textAlign: "center" }}>
                                        <button
                                            className={"button-face export-state-" + exportState}
                                            disabled={exportResults.totalResults > MaxResultsPerReport}
                                            onClick={downloadExportResults}
                                        >
                                            {exportResults.totalResults > MaxResultsPerReport ?
                                                <>Export too large</>
                                                :
                                                <>Export report in .csv format</>
                                            }
                                        </button>
                                    </div>
                                </div>
                            </>
                        }
                    </>
                }

            </div>

        </PortalPage>
    );
};

export default UserDocumentReport;
